import { auth } from 'shared/services/firebase';
import { logGlobalError } from 'shared/utils/globalErrorLogger';
import { UserAccount } from 'shared/types/userAccount';
import { ensureExternalPlatformAccountForUser } from 'domains/experts/data/externalPlatform';

/**
 * Session backstop for the external-platform account that onboarding creates.
 *
 * Onboarding fires that creation without awaiting it, so an expert who closes
 * the tab on the last step can land in the app fully onboarded but with no
 * `external_platform_accounts` doc — and therefore no aligned email address,
 * which every later campaign-onboarding step assumes exists. This runs at most
 * once per account per page session and creates the missing account.
 *
 * Mirrors aligned's `UserService._ensureExternalPlatformAccountExists`.
 */

const checkedUserAccountIds = new Set<string>();

/** Test seam: clears the once-per-session guard. */
export function resetExternalPlatformSessionGuard(): void {
  checkedUserAccountIds.clear();
}

export async function ensureExternalPlatformAccountForSession(account: UserAccount): Promise<void> {
  if (checkedUserAccountIds.has(account.id)) return;
  // Only onboarded experts are supposed to have one; creating an account for
  // somebody still mid-onboarding would race the onboarding flow's own call.
  if (!account.raterOnboardingComplete) return;
  if (!account.firebaseId || !account.email) return;

  // The account doc arrives from a live subscription, so this can run a tick
  // after a sign-out. Writing then would fail against revoked auth; leaving the
  // guard clear lets a later snapshot retry once auth has settled.
  if (auth.currentUser?.uid !== account.firebaseId) return;

  checkedUserAccountIds.add(account.id);
  try {
    await ensureExternalPlatformAccountForUser(account);
  } catch (err) {
    logGlobalError(err, {
      context: 'ensureExternalPlatformAccountForSession',
      metadata: { userAccountId: account.id },
    });
  }
}
